import { useEffect, useMemo, useRef } from 'react';

/**
 * Candado de cierre del modal abierto mientras GUARDA (QA 15-oct, T1.11+a).
 *
 * Con la red lenta, un clic fuera del recuadro (o Escape, o la X) cerraba
 * «Registrar parada» a mitad de guardar; si luego fallaba, solo quedaba el
 * toast del error y todo lo escrito —paradas marcadas, PK, causa,
 * descripción— se había perdido. Mientras un modal sostiene el candado,
 * `SuperModal` ignora los cierres del usuario; el propio modal lo suelta antes
 * de cerrarse al acabar bien.
 *
 * Vive fuera de Redux a propósito: el `handleClickClose` que reciben los
 * modales se crea con el estado del render en que se abrió, así que un flag en
 * el estado del modal llegaría tarde (el cierre al acabar bien vería todavía
 * el candado puesto). Aquí se consulta en el momento del cierre.
 */
let holders = 0;

/** Echa el candado; devuelve con qué soltarlo (soltar dos veces no hace nada). */
export function lockModalClose(): () => void {
  holders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
  };
}

/** ¿Hay un modal guardando? Lo consulta `SuperModal` antes de cerrar. */
export function isModalCloseLocked(): boolean {
  return holders > 0;
}

/**
 * El candado desde un modal: `lock()` al empezar a guardar y `unlock()` al
 * acabar, ANTES de cerrarse. Si el modal se desmonta con el candado puesto
 * (otra pantalla lo cierra), se suelta solo.
 */
export function useModalCloseLock(): { lock: () => void; unlock: () => void } {
  const release = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      release.current?.();
      release.current = null;
    },
    []
  );
  return useMemo(
    () => ({
      lock: () => {
        release.current?.();
        release.current = lockModalClose();
      },
      unlock: () => {
        release.current?.();
        release.current = null;
      },
    }),
    []
  );
}
